import React, { useState } from 'react';
import mathHelper from '../../Helpers/mathHelper';
import TierInputs from './TierInputs';

function CheckAllIcon({ size = 12 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth={1.7}
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='M2 13l4 4 8-9' />
      <path d='M11 16l1 1 8-9' />
    </svg>
  );
}

const parseCount = (rawValue) => {
  const parsed = parseInt(rawValue, 10);
  return isNaN(parsed) ? 0 : Math.max(0, parsed);
};

const clampRate = (rawValue) => {
  const parsed = parseInt(rawValue, 10);
  return Math.max(0, Math.min(100, isNaN(parsed) ? 0 : parsed));
};

// Bravo per-country planning editor. value rows: [{pricingId, country,
// numberOfPlannedAssignments, applyTargetRate, targetAssignmentRate, targetCompletionRate}].
// The defaults strip is local UI state; save-time validation stays in the window description.
//
// One block per country rather than one wide row. The eleven columns this used to be never
// fit a phone, and scrolling them sideways carried the country name off screen — leaving a
// row of unlabelled rate boxes with nothing to say which country was being edited. Every
// group here wraps instead, so the whole editor works at any width without scrolling
// sideways, and each value keeps its caption wherever it lands.
function CountryPlanningGrid({ value, onChange }) {
  const rows = value ?? [];
  const [defaultShopCount, setDefaultShopCount] = useState(0);
  const [defaultAssignmentRate, setDefaultAssignmentRate] = useState([30, 45, 25, 0]);
  const [defaultCompletionRate, setDefaultCompletionRate] = useState([20, 30, 30, 20]);

  const allApply = rows.length > 0 && rows.every((row) => row.applyTargetRate);
  const someApply = rows.some((row) => row.applyTargetRate) && !allApply;

  const updateRow = (index, changes) => onChange(rows.map((row, i) => (i === index ? { ...row, ...changes } : row)));
  const setRowTier = (index, key, tier, rawValue) =>
    updateRow(index, { [key]: rows[index][key].map((v, t) => (t === tier ? clampRate(rawValue) : v)) });
  const setDefaultTier = (setter, current, tier, rawValue) =>
    setter(current.map((v, t) => (t === tier ? clampRate(rawValue) : v)));

  const toggleAllApply = () => onChange(rows.map((row) => ({ ...row, applyTargetRate: !allApply })));
  const applyPlannedToAll = () =>
    onChange(rows.map((row) => ({ ...row, numberOfPlannedAssignments: defaultShopCount })));
  const applyAssignmentToAll = () =>
    onChange(rows.map((row) => ({ ...row, applyTargetRate: true, targetAssignmentRate: [...defaultAssignmentRate] })));
  const applyCompletionToAll = () =>
    onChange(rows.map((row) => ({ ...row, applyTargetRate: true, targetCompletionRate: [...defaultCompletionRate] })));

  return (
    <div className='bravo-ds mie-host'>
      <div className='mie-defaults'>
        <div className='mie-defaults-head'>
          <span className='ic'>
            <CheckAllIcon size={15} />
          </span>
          <b>Set defaults</b>
          <span>— enter a value, then push it to every country below.</span>
        </div>
        <div className='mie-def-row'>
          <div className='mie-def-block'>
            <span className='mie-th'>Planned shops</span>
            <input
              className='mie-num mie-planned'
              type='number'
              min='0'
              value={mathHelper.RemoveLeadingZeros(defaultShopCount)}
              onChange={(e) => setDefaultShopCount(parseCount(e.target.value))}
              aria-label='Default planned shops'
            />
            <button
              type='button'
              className='apply-all'
              disabled={defaultShopCount < 1}
              onClick={applyPlannedToAll}
            >
              <CheckAllIcon /> Apply to all
            </button>
          </div>
          <div className='mie-def-block'>
            <span className='mie-th'>Target assignment rate (%)</span>
            <TierInputs
              showTierLabels
              values={defaultAssignmentRate}
              onSet={(tier, v) => setDefaultTier(setDefaultAssignmentRate, defaultAssignmentRate, tier, v)}
            />
            <button
              type='button'
              className='apply-all'
              disabled={mathHelper.Sum(defaultAssignmentRate) !== 100}
              onClick={applyAssignmentToAll}
            >
              <CheckAllIcon /> Apply to all
            </button>
          </div>
          <div className='mie-def-block'>
            <span className='mie-th'>Target completion rate (%)</span>
            <TierInputs
              showTierLabels
              values={defaultCompletionRate}
              onSet={(tier, v) => setDefaultTier(setDefaultCompletionRate, defaultCompletionRate, tier, v)}
            />
            <button
              type='button'
              className='apply-all'
              disabled={mathHelper.Sum(defaultCompletionRate) !== 100}
              onClick={applyCompletionToAll}
            >
              <CheckAllIcon /> Apply to all
            </button>
          </div>
        </div>
      </div>

      <div className='mie-list-head'>
        <span className='mie-th'>{`${rows.length} ${rows.length === 1 ? 'country' : 'countries'}`}</span>
        <label className='mie-check'>
          <input
            className='cbx'
            type='checkbox'
            checked={allApply}
            ref={(el) => {
              if (el) el.indeterminate = someApply;
            }}
            onChange={toggleAllApply}
          />
          Apply target rates to all
        </label>
      </div>

      <div className='mie-cards'>
        {rows.map((row, index) => (
          <div
            key={`countryRow${index}`}
            className={row.applyTargetRate ? 'mie-card' : 'mie-card off'}
          >
            <div className='mie-card-head'>
              <div className='mie-country'>
                <b>{row.country}</b>
                <span>{row.pricingId}</span>
              </div>
              <label className='mie-field'>
                <span className='mie-th'>Planned shops</span>
                <input
                  className='mie-num mie-planned'
                  type='number'
                  min='0'
                  value={mathHelper.RemoveLeadingZeros(row.numberOfPlannedAssignments)}
                  onChange={(e) => updateRow(index, { numberOfPlannedAssignments: parseCount(e.target.value) })}
                  aria-label={`${row.country} planned shops`}
                />
              </label>
              <label className='mie-check'>
                <input
                  className='cbx'
                  type='checkbox'
                  checked={row.applyTargetRate}
                  onChange={(e) => updateRow(index, { applyTargetRate: e.target.checked })}
                />
                Apply target rates
              </label>
            </div>
            <div className='mie-card-rates'>
              <div className='mie-rate-group'>
                <span className='mie-th'>Target assignment rate (%)</span>
                <TierInputs
                  showTierLabels
                  values={row.targetAssignmentRate}
                  disabled={!row.applyTargetRate}
                  onSet={(tier, v) => setRowTier(index, 'targetAssignmentRate', tier, v)}
                />
              </div>
              <div className='mie-rate-group'>
                <span className='mie-th'>Target completion rate (%)</span>
                <TierInputs
                  showTierLabels
                  values={row.targetCompletionRate}
                  disabled={!row.applyTargetRate}
                  onSet={(tier, v) => setRowTier(index, 'targetCompletionRate', tier, v)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CountryPlanningGrid;
